import * as THREE from 'three';

/**
 * Cohesive per-planet color palette.
 *
 * Everything visible on a planet — terrain, flora, rocks, crystals,
 * creatures — samples from ONE palette derived from the planet's base hue.
 * That shared structure is what makes a scene read as art-directed instead
 * of independently-randomized. Structure:
 *
 *   terrain — the planet's base hue (already used by the terrain shader)
 *   flora   — analogous hue (+0.08), sits harmoniously next to terrain
 *   accent  — near-triadic hue (+0.38), used sparingly: crystals, glows,
 *             and most creatures, so life "pops" against the ground
 */
export function planetPalette(baseHue) {
  const h = ((baseHue % 1) + 1) % 1;
  return {
    terrain: h,
    flora: (h + 0.08) % 1,
    accent: (h + 0.38) % 1,
  };
}

/**
 * Pick a creature color from a planet palette.
 * Creatures mostly wear the accent hue (they should stand out from terrain),
 * occasionally the flora hue (camouflage species), with tight S/L ranges so
 * no species is muddy or blinding.
 * Consumes 4 rng calls — deterministic per species.
 */
export function creatureColorFromPalette(pal, rng) {
  const nearAccent = rng.next() < 0.7;
  const base = nearAccent ? pal.accent : pal.flora;
  const hue = (base + (rng.next() - 0.5) * 0.1 + 1) % 1;
  const s = 0.5 + rng.next() * 0.3;
  const l = 0.55 + rng.next() * 0.15;
  return new THREE.Color().setHSL(hue, s, l).getHex();
}

/**
 * Pull a material's color (and emissive, if set) toward the planet's hue.
 * Mirrors the hue shift the terrain shader already applies, so scatter
 * props stop looking pasted-on. `visited` prevents double-shifting shared
 * materials.
 */
export function pullMaterialTowardHue(material, planetHue, visited, strength = 0.15) {
  if (!material || visited.has(material)) return;
  visited.add(material);
  const hsl = {};
  const shift = (planetHue - 0.5) * strength;
  if (material.color) {
    material.color.getHSL(hsl);
    material.color.setHSL((hsl.h + shift + 1) % 1, hsl.s, hsl.l);
  }
  if (material.emissive && (material.emissive.r || material.emissive.g || material.emissive.b)) {
    material.emissive.getHSL(hsl);
    material.emissive.setHSL((hsl.h + shift + 1) % 1, hsl.s, hsl.l);
  }
}
